import { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { GlobalSearch } from './GlobalSearch';
import { AIConsultant } from './AIConsultant';
import { BackButton } from './BackButton';
import OnboardingTour from './OnboardingTour';
import GlobalShortcutsLayer from './GlobalShortcutsLayer';
import { moduleItems } from './layout/navConfig';
import { SimpleDropdown, DropdownItem } from './layout/SimpleDropdown';
import { useVBPendingCount } from '../hooks/useVBPendingCount';
import { useRecepcionesAnuladasCount } from '../hooks/useRecepcionesAnuladasCount';
import { useOCPendientesRecepcionCount } from '../hooks/useOCPendientesRecepcionCount';
import { useHealthStaleCount } from '../hooks/useHealthStaleCount';
import { useAlegraFreshness } from '../hooks/useAlegraFreshness';
import { useAprobacionesPendientes } from '../hooks/useAprobacionesPendientes';
import { useFavoritos } from '../hooks/useFavoritos';
import {
  Menu,
  X,
  LogOut,
  ChevronRight,
  ChevronDown,
  TrendingUp,
  Mail,
  Sun,
  Moon,
  Star,
  Sparkles,
} from 'lucide-react';

// Re-exportamos `moduleItems` para mantener compatibilidad con consumidores
// existentes que importan desde `./Layout` (ej: GestionUsuarios.jsx).
// Iter 387: items del menú pueden traer query (?lado=...) — se compara solo el pathname.
const isPathActive = (pathname, path) => { const base = (path || '').split('?')[0]; return pathname === base || pathname.startsWith(base + '/'); };

export { moduleItems };

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [expandedModules, setExpandedModules] = useState(['informacion']);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('chirino-dark-mode');
    return saved === 'true';
  });
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const vbPendingCount = useVBPendingCount();
  const recepcionesAnuladasCount = useRecepcionesAnuladasCount();
  const ocPendientesRecepcion = useOCPendientesRecepcionCount();
  const healthStaleCount = useHealthStaleCount();
  const alegraFreshness = useAlegraFreshness();
  const aprobacionesCounts = useAprobacionesPendientes();
  const { isFav, toggle: toggleFav, favoritos } = useFavoritos();

  // Sync dark mode with DOM
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('chirino-dark-mode', darkMode);
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(prev => !prev);

  // Filter moduleItems based on user permissions
  const filteredModuleItems = useMemo(() => {
    if (!user) return moduleItems.filter((m) => !m.adminOnly);
    const rol = user.rol || 'consulta';
    const permisos = user.permisos || {};

    // Admin sees everything
    if (['admin', 'administrador'].includes((user?.rol || '').toLowerCase())) return moduleItems;

    // Eliminar items adminOnly para no-admin
    const visibleItems = moduleItems.filter((m) => !m.adminOnly);

    // Aplica lista blanca de paths o filtro por acciones granulares (NEW).
    const applyPathsFilter = (item) => {
      const modPerm = permisos[item.id] || {};
      const acciones = modPerm.acciones || {};
      const paths = Array.isArray(modPerm.paths) ? modPerm.paths : null;
      let subItems = item.subItems || [];

      // Filter por acción granular: si el sub-item declara `action`, debe estar habilitada
      // (o el usuario tiene editar/vb del módulo, o rol admin/gerencia).
      const hasModuleEditOrVb = !!(modPerm.editar || modPerm.vb);
      const isPrivileged = (rol === 'admin' || rol === 'gerencia');
      subItems = subItems.filter(sub => {
        // Sub-items marcados adminOnly: solo admin/gerencia/rrhh_encargado
        if (sub.adminOnly && !(rol === 'admin' || rol === 'gerencia' || rol === 'rrhh_encargado')) {
          return false;
        }
        if (!sub.action) return true;
        return isPrivileged || hasModuleEditOrVb || acciones[sub.action] === true;
      });

      // Lista blanca de paths: si está definida (incluso vacía), respeta esa selección.
      // Si está vacía explícitamente → módulo oculto (subItems quedará en []).
      if (paths !== null) {
        subItems = subItems.filter(sub => paths.includes(sub.path));
      }
      return { ...item, subItems };
    };

    // Terreno: fallback al comportamiento legacy si no hay paths configurados.
    if (rol === 'terreno') {
      const terrenoPerms = permisos.rrhh || {};
      const paths = Array.isArray(terrenoPerms.paths) && terrenoPerms.paths.length > 0
        ? terrenoPerms.paths
        : ['/rrhh/parte-diario', '/rrhh/monitor-partes', '/rrhh/resumen-almuerzos'];
      const rrhhMenu = moduleItems
        .filter(item => item.id === 'rrhh')
        .map(item => ({
          ...item,
          subItems: item.subItems?.filter(sub => paths.includes(sub.path))
        }));
      // Iter 236.w (Renzo, feb-2026): usuario terreno puede tener acceso a más
      // sub-items de Compras que solo Requisiciones (ej. Rendiciones de Gastos
      // para Álvaro Jara). Si `permisos.compras.paths` está configurado, se
      // respetan TODOS esos paths; si no está seteado, fallback legacy solo
      // Requisiciones (Carlos: "los dejaria a todos con acceso a crear requisiciones").
      const comprasModule = moduleItems.find(item => item.id === 'compras');
      const comprasPathsCfg = Array.isArray(permisos.compras?.paths) ? permisos.compras.paths : null;
      const comprasFallbackPaths = ['/compras/requisiciones'];
      const comprasAllowedPaths = comprasPathsCfg && comprasPathsCfg.length > 0
        ? comprasPathsCfg
        : comprasFallbackPaths;
      const comprasSubItemsVisibles = (comprasModule?.subItems || []).filter(
        (s) => comprasAllowedPaths.includes(s.path)
      );
      const comprasMenu = comprasSubItemsVisibles.length > 0
        ? [{
            ...comprasModule,
            label: 'Compras',
            subItems: comprasSubItemsVisibles,
          }]
        : [];
      // Iter 236.z (Renzo, feb-2026): usuario terreno puede tener acceso ADEMÁS
      // a OTROS módulos si Renzo le configuró `permisos[id].ver=true` + `paths[]`
      // (ej. Álvaro Jara con `informacion.ver=true` + `paths=['/ordenes-trabajo','/proyectos']`
      // para consultar sin editar). Se respetan los paths configurados y no se
      // muestran módulos vacíos.
      const OTROS_MODULOS = ['informacion', 'operaciones', 'contabilidad', 'bodega', 'alegra'];
      const otrosMenus = [];
      for (const modId of OTROS_MODULOS) {
        const modPerms = permisos[modId];
        if (!modPerms?.ver) continue;
        const paths = Array.isArray(modPerms.paths) ? modPerms.paths : null;
        if (!paths || paths.length === 0) continue;
        const modItem = moduleItems.find(m => m.id === modId);
        if (!modItem) continue;
        const subItemsVisibles = (modItem.subItems || []).filter(s => paths.includes(s.path));
        if (subItemsVisibles.length === 0) continue;
        otrosMenus.push({ ...modItem, subItems: subItemsVisibles });
      }
      return [...otrosMenus, ...comprasMenu, ...rrhhMenu];
    }

    return visibleItems
      .filter((item) => {
        // Dashboard always visible
        if (item.path === '/dashboard') return true;
        // Access DB hidden for non-admin unless permission
        if (item.path === '/access-database') return permisos.access?.ver === true;
        // Module items: check permisos[id].ver
        if (item.type === 'module' && item.id) {
          return permisos[item.id]?.ver === true;
        }
        // Configuración: visible si tiene permisos.configuracion.ver === true
        if (item.id === 'configuracion' || item.type === 'menu') {
          return permisos.configuracion?.ver === true;
        }
        return true;
      })
      .map(item => (item.type === 'module' && item.id ? applyPathsFilter(item) : item))
      .filter(item => {
        // Ocultar módulos cuyo filtrado de paths dejó la lista vacía
        if (item.type === 'module' && Array.isArray(item.subItems) && item.subItems.length === 0) {
          return false;
        }
        return true;
      });
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-expandir solo el módulo activo
  useEffect(() => {
    let activeModuleId = null;
    filteredModuleItems.forEach((item) => {
      if ((item.type === 'module' || item.type === 'menu') && item.subItems) {
        const isActive = item.subItems.some(
          (sub) => isPathActive(location.pathname, sub.path)
        );
        if (isActive) {
          activeModuleId = item.id || item.label;
        }
      }
    });
    if (activeModuleId) {
      setExpandedModules([activeModuleId]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const toggleModule = (moduleId) => {
    setExpandedModules((prev) =>
      prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [moduleId]
    );
  };

  // Item simple (sin submódulos)
  const SingleNavItem = ({ item, onClick }) => {
    const isActive = isPathActive(location.pathname, item.path);
    const Icon = item.icon;
    const badge = item.path === '/aprobaciones' && aprobacionesCounts.total > 0 ? aprobacionesCounts.total : null;

    return (
      <Link
        to={item.path}
        onClick={onClick}
        data-testid={`nav-${item.path.replace(/\//g, '-')}`}
        className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-300 text-sm ${
          isActive
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
        }`}
      >
        <div className="relative flex-shrink-0">
          <Icon className="w-4 h-4" strokeWidth={1.5} />
          {badge !== null && !sidebarOpen && (
            <span
              className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-rose-500 border border-white"
              data-testid="badge-aprobaciones-collapsed"
            />
          )}
        </div>
        <span className={`font-medium ${!sidebarOpen && 'lg:hidden'}`}>{item.label}</span>
        {badge !== null && sidebarOpen && (
          <span
            className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white tabular-nums"
            data-testid="badge-aprobaciones-sidebar"
          >
            {badge}
          </span>
        )}
      </Link>
    );
  };

  // Módulo con submódulos
  const ModuleNavItem = ({ item, onClick }) => {
    const isExpanded = expandedModules.includes(item.id);
    const hasActiveChild = item.subItems?.some(
      (sub) => isPathActive(location.pathname, sub.path)
    );
    const Icon = item.icon;
    const moduleBadge = (() => {
      if (item.id === 'rrhh' && vbPendingCount > 0) return vbPendingCount;
      if (item.id === 'configuracion' && healthStaleCount > 0) return healthStaleCount;
      if (item.id === 'bodega') {
        const total = (recepcionesAnuladasCount || 0) + (ocPendientesRecepcion?.count || 0);
        if (total > 0) return total;
      }
      return null;
    })();

    return (
      <div className="space-y-1">
        {/* Header del módulo */}
        <button
          type="button"
          onClick={() => toggleModule(item.id)}
          data-testid={`module-${item.id}`}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-300 text-sm ${
            hasActiveChild
              ? 'bg-primary/10 text-primary font-semibold'
              : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
          }`}
        >
          <div className="relative flex-shrink-0">
            <Icon className="w-4 h-4" strokeWidth={1.5} />
            {moduleBadge !== null && !sidebarOpen && (
              <span
                data-testid="badge-vb-pendientes-collapsed"
                className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[14px] h-[14px] px-0.5 rounded-full text-[9px] font-bold bg-rose-500 text-white"
              >
                {moduleBadge > 9 ? '9+' : moduleBadge}
              </span>
            )}
          </div>
          <span className={`font-medium flex-1 text-left ${!sidebarOpen && 'lg:hidden'}`}>
            {item.label}
          </span>
          {sidebarOpen && moduleBadge !== null && !isExpanded && (
            <span
              data-testid="badge-vb-pendientes-module"
              className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-rose-500 text-white"
            >
              {moduleBadge > 99 ? '99+' : moduleBadge}
            </span>
          )}
          {sidebarOpen && (
            isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )
          )}
        </button>

        {/* Submódulos */}
        {isExpanded && sidebarOpen && item.subItems && (
          <div className="ml-4 pl-3 border-l border-border/50 space-y-0.5">
            {(() => {
              let lastGroup = null;
              const nodes = [];
              item.subItems.forEach((subItem) => {
                // Renderizar separador de grupo
                if (subItem.group && subItem.group !== lastGroup) {
                  const groupSlug = subItem.group.toLowerCase().replace(/[^a-z0-9áéíóúñ]+/gi, '-').replace(/^-|-$/g, '');
                  nodes.push(
                    <div
                      key={`grp-${item.id}-${subItem.group}`}
                      className="px-2.5 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60"
                      data-testid={`group-${groupSlug}`}
                    >
                      {subItem.group}
                    </div>
                  );
                  lastGroup = subItem.group;
                }
                const isActive = isPathActive(location.pathname, subItem.path);
                const SubIcon = subItem.icon;
                const badge = (() => {
                  if (subItem.path === '/rrhh/mis-vb' && vbPendingCount > 0) return vbPendingCount;
                  if (subItem.path === '/configuracion/health-check' && healthStaleCount > 0) return healthStaleCount;
                  if (subItem.path === '/bodega/recepciones' && recepcionesAnuladasCount > 0) return recepcionesAnuladasCount;
                  if (subItem.path === '/bodega/oc-pendientes-recepcion' && ocPendientesRecepcion?.count > 0) return ocPendientesRecepcion.count;
                  return null;
                })();
                const fav = isFav(subItem.path);
                nodes.push(
                  <div key={subItem.path} className="group/sub flex items-center gap-1">
                    <Link
                      to={subItem.path}
                      onClick={onClick}
                      data-testid={`nav-${subItem.path.replace(/\//g, '-')}`}
                      className={`flex-1 flex items-center gap-2 px-2.5 py-1.5 rounded-lg transition-all duration-300 text-xs ${
                        isActive
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                      }`}
                    >
                      <SubIcon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.5} />
                      <span className="flex-1 truncate">{subItem.label}</span>
                      {badge !== null && (
                        <span
                          data-testid="badge-vb-pendientes-sub"
                          className={`ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${
                            isActive ? 'bg-white text-primary' : 'bg-rose-500 text-white'
                          }`}
                        >
                          {badge > 99 ? '99+' : badge}
                        </span>
                      )}
                    </Link>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(subItem.path); }}
                      title={fav ? 'Quitar de favoritos' : 'Añadir a favoritos'}
                      data-testid={`fav-toggle-${subItem.path.replace(/\//g, '-')}`}
                      className={`flex-shrink-0 p-1 rounded transition-all ${
                        fav
                          ? 'text-amber-500'
                          : 'text-muted-foreground/30 opacity-0 group-hover/sub:opacity-100 hover:text-amber-500'
                      }`}
                    >
                      <Star className="w-3 h-3" fill={fav ? 'currentColor' : 'none'} strokeWidth={2} />
                    </button>
                  </div>
                );
              });
              return nodes;
            })()}
          </div>
        )}
      </div>
    );
  };

  const renderNavItem = (item, onClick) => {
    if (item.type === 'module' || item.type === 'menu') {
      return <ModuleNavItem key={item.id || item.label} item={{...item, id: item.id || item.label}} onClick={onClick} />;
    }
    return <SingleNavItem key={item.path} item={item} onClick={onClick} />;
  };

  // Items marcados como favoritos por el usuario.
  // Recorremos filteredModuleItems y devolvemos solo los subItems cuyos paths
  // están en el Set `favoritos`. Así respetamos los permisos del usuario
  // (un fav que el usuario ya no tiene permiso, simplemente no aparece).
  const favoriteItems = useMemo(() => {
    if (!favoritos.size) return [];
    const out = [];
    filteredModuleItems.forEach((mod) => {
      if (Array.isArray(mod.subItems)) {
        mod.subItems.forEach((s) => {
          if (favoritos.has(s.path)) out.push({ ...s, _moduleLabel: mod.label });
        });
      }
    });
    return out;
  }, [favoritos, filteredModuleItems]);

  const FavoritesSection = ({ onClick }) => {
    if (!favoriteItems.length || !sidebarOpen) return null;
    return (
      <div className="mb-2 pb-2 border-b border-border/40" data-testid="sidebar-favoritos">
        <div className="flex items-center gap-1.5 px-2.5 pt-1 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
          <Star className="w-3 h-3" fill="currentColor" /> Mis favoritos
        </div>
        <div className="space-y-0.5">
          {favoriteItems.map((sub) => {
            const isActive = isPathActive(location.pathname, sub.path);
            const SubIcon = sub.icon;
            return (
              <div key={`fav-${sub.path}`} className="group/fav flex items-center gap-1">
                <Link
                  to={sub.path}
                  onClick={onClick}
                  data-testid={`fav-nav-${sub.path.replace(/\//g, '-')}`}
                  className={`flex-1 flex items-center gap-2 px-2.5 py-1.5 rounded-lg transition-all duration-300 text-xs ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground hover:bg-muted/60'
                  }`}
                  title={`${sub._moduleLabel} → ${sub.label}`}
                >
                  <SubIcon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.5} />
                  <span className="flex-1 truncate">{sub.label}</span>
                </Link>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(sub.path); }}
                  title="Quitar de favoritos"
                  data-testid={`fav-remove-${sub.path.replace(/\//g, '-')}`}
                  className="flex-shrink-0 p-1 rounded text-amber-500 hover:text-amber-600"
                >
                  <Star className="w-3 h-3" fill="currentColor" strokeWidth={2} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 glass z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className="rounded-xl"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <span className="font-heading font-bold text-lg tracking-tight">CHIRINO STEEL</span>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Dark mode toggle mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl h-8 w-8"
            onClick={() => toggleDarkMode()}
            data-testid="dark-mode-toggle-mobile"
          >
            <Sun className="w-4 h-4 dark:hidden" />
            <Moon className="w-4 h-4 hidden dark:block" />
          </Button>
          {/* Mobile User Menu */}
          <div className="relative" ref={userMenuRef}>
            <Button 
              variant="ghost" 
              className="relative h-8 w-8 rounded-full" 
              data-testid="user-menu-mobile"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {getInitials(user?.nombre)}
                </AvatarFallback>
              </Avatar>
            </Button>
            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-popover border rounded-2xl shadow-lg py-1 z-50">
                <div className="px-4 py-3 border-b">
                  <p className="text-sm font-medium">{user?.nombre}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setUserMenuOpen(false); window.dispatchEvent(new CustomEvent('chirino-show-onboarding')); }}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted/50 flex items-center transition-colors duration-200"
                  data-testid="show-tutorial-mobile"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Ver tutorial
                </button>
                <button
                  type="button"
                  onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted/50 flex items-center transition-colors duration-200"
                  data-testid="logout-mobile"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar Sesion
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity duration-300"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`lg:hidden fixed top-14 left-0 bottom-0 w-72 glass z-50 transform transition-transform duration-300 overflow-y-auto ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <nav className="py-4 px-3 space-y-1">
          <FavoritesSection onClick={() => setMobileMenuOpen(false)} />
          {filteredModuleItems.map((item) => renderNavItem(item, () => setMobileMenuOpen(false)))}
        </nav>
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed top-0 left-0 bottom-0 glass z-40 transition-all duration-300 ${
          sidebarOpen ? 'w-[272px]' : 'w-16'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border/50">
          <div className={`flex items-center gap-3 ${!sidebarOpen && 'hidden'}`}>
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-sm">
              <span className="font-heading font-bold text-primary-foreground text-sm">CS</span>
            </div>
            <div className="flex flex-col">
              <span className="font-heading font-bold text-sm tracking-tight leading-none">CHIRINO STEEL</span>
              <span className="text-[10px] text-muted-foreground mt-0.5">Sistema de Gestion</span>
            </div>
          </div>
          {/* Toggle button: SIEMPRE visible para poder reabrir el sidebar.
              Cuando está cerrado, ocupa todo el ancho centrado. */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`h-7 w-7 rounded-lg ${!sidebarOpen && 'mx-auto'}`}
            title={sidebarOpen ? 'Ocultar menú' : 'Mostrar menú'}
            data-testid="sidebar-toggle"
          >
            <Menu className="w-4 h-4" />
          </Button>
        </div>

        {/* Global Search trigger (desktop) */}
        {sidebarOpen && (
          <div className="px-3 pt-3">
            <GlobalSearch />
          </div>
        )}

        {/* Indicador de frescura de Alegra (sidebar abierto) */}
        {sidebarOpen && alegraFreshness.lastRun && (
          <Link
            to="/configuracion/health-check"
            className="mx-3 mt-2 mb-1 px-2.5 py-1.5 flex items-center gap-1.5 rounded-md text-[10px] hover:bg-muted/60 transition-colors"
            title={`Último sync incremental Alegra: ${new Date(alegraFreshness.lastRun).toLocaleString('es-CL')}. Click para ver Health Check.`}
            data-testid="alegra-freshness-indicator"
          >
            <span className={`relative flex h-1.5 w-1.5`}>
              {alegraFreshness.status === 'fresh' && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              )}
              <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                alegraFreshness.status === 'fresh' ? 'bg-emerald-500' :
                alegraFreshness.status === 'stale' ? 'bg-amber-500' : 'bg-slate-400'
              }`}></span>
            </span>
            <span className="text-muted-foreground tabular-nums">
              Alegra ·{' '}
              {alegraFreshness.minutesAgo === 0 ? 'ahora' :
               alegraFreshness.minutesAgo < 60 ? `${alegraFreshness.minutesAgo} min` :
               `${Math.floor(alegraFreshness.minutesAgo / 60)}h ${alegraFreshness.minutesAgo % 60}m`}
            </span>
          </Link>
        )}

        {/* Navigation */}
        <div className="flex-1 min-h-0 py-3 px-3 overflow-y-auto scrollbar-thin">
          <nav className="space-y-0.5">
            <FavoritesSection onClick={undefined} />
            {filteredModuleItems.map((item) => renderNavItem(item, undefined))}
          </nav>
        </div>

        {/* Dark mode toggle + User Section */}
        <div className="p-3 border-t border-border/50 space-y-2">
          {sidebarOpen && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 rounded-xl text-xs text-muted-foreground hover:text-foreground"
              onClick={() => toggleDarkMode()}
              data-testid="dark-mode-toggle"
            >
              <Sun className="w-3.5 h-3.5 dark:hidden" />
              <Moon className="w-3.5 h-3.5 hidden dark:block" />
              <span className="dark:hidden">Modo Oscuro</span>
              <span className="hidden dark:inline">Modo Claro</span>
            </Button>
          )}
          <SimpleDropdown
            align="start"
            trigger={
              <Button
                variant="ghost"
                className={`w-full justify-start gap-3 h-auto py-2.5 rounded-xl ${!sidebarOpen && 'justify-center px-2'}`}
                data-testid="user-menu-desktop"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                    {getInitials(user?.nombre)}
                  </AvatarFallback>
                </Avatar>
                {sidebarOpen && (
                  <div className="flex flex-col items-start text-left">
                    <span className="text-xs font-medium truncate max-w-[140px]">{user?.nombre}</span>
                    <span className="text-[10px] text-muted-foreground capitalize">{user?.rol}</span>
                  </div>
                )}
              </Button>
            }
          >
            <div className="px-4 py-3 border-b">
              <p className="text-sm font-medium">{user?.nombre}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <DropdownItem
              onClick={() => { if (typeof window.__openShortcutsHelp === 'function') window.__openShortcutsHelp(true); }}
              data-testid="show-shortcuts-desktop"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Atajos de teclado
              <span className="ml-auto text-[10px] text-muted-foreground font-mono">?</span>
            </DropdownItem>
            <DropdownItem
              onClick={() => window.dispatchEvent(new CustomEvent('chirino-show-onboarding'))}
              data-testid="show-tutorial-desktop"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Ver tutorial
            </DropdownItem>
            <DropdownItem onClick={handleLogout} data-testid="logout-desktop">
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesion
            </DropdownItem>
          </SimpleDropdown>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`min-h-screen pt-14 lg:pt-0 transition-all duration-300 ${
          sidebarOpen ? 'lg:pl-[272px]' : 'lg:pl-16'
        }`}
      >
        {/* Botón global "Volver" — visible si no estamos en una ruta raíz */}
        <BackButton />
        <div className="p-4 lg:p-6 pb-20 page-enter">{children}</div>
      </main>

      {/* Mounted globally — keyboard shortcut Cmd+K works always */}
      <GlobalSearch showTrigger={false} />

      {/* Asistente IA flotante (botón abajo a la derecha) */}
      <AIConsultant />

      {/* Mini-tutorial primer login (auto-aparece + botón "Ver tutorial" en menú usuario) */}
      <OnboardingTour />

      {/* Atajos de teclado globales (g+f, c+o, ? para ayuda) */}
      <GlobalShortcutsLayer />
    </div>
  );
};
